// frontend/app/components/lessonPlans/authoring/WizardStepPhases.js
//
// Step 3: the lesson flow — its steps, and the content inside each.
//
// Authors only ever see the four sections. The section vocabulary is never
// written down here: it is SECTION_META, the same map the rail renders from.
// A step's phase stays as a hidden key (the section's first phase).

import React, { useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import WizardPhaseCard from "./WizardPhaseCard";
import { defaultLoopSteps, firstPhaseKey } from "./lessonDraft";
import { SECTION_META } from "../lessonPlanTheme";

const SECTION_OPTIONS = Object.entries(SECTION_META).map(([value, meta]) => ({
  value,
  label: meta.label,
  accent: meta.accent,
}));

const WizardStepPhases = ({
  colors,
  steps = [],
  concepts = [],
  boardTopicId,
  catalog,
  onChange,
  errors = {},
  t,
}) => {
  const translate = t || ((key) => key);
  const [expandedKey, setExpandedKey] = useState(steps[0]?.step_key || null);

  const uniqueStepKey = (base) => {
    const taken = new Set(steps.map((s) => s.step_key));
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base}_${n}`)) n += 1;
    return `${base}_${n}`;
  };

  const addStep = (phase = firstPhaseKey()) => {
    const key = uniqueStepKey(phase);
    const next = [
      ...steps,
      {
        step_key: key,
        phase,
        instructional_mode: "WE_DO",
        title: "",
        blocks: [],
        completion_rules: [],
      },
    ];
    onChange(next);
    setExpandedKey(key);
  };

  const useLoopFlow = () => {
    const next = defaultLoopSteps(translate);
    onChange(next);
    setExpandedKey(next[0]?.step_key || null);
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    next[index] = steps[target];
    next[target] = steps[index];
    onChange(next);
  };

  return (
    <View>
      <View style={styles.introRow}>
        <View style={{ flex: 1 }}>
          <Text style={[styles.introTitle, { color: colors.textPrimary }]}>
            {translate("Lesson flow")}
          </Text>
          <Text style={[styles.introSubtitle, { color: colors.textMuted }]}>
            {steps.length} {translate(steps.length === 1 ? "step" : "steps")}
          </Text>
        </View>
        {steps.length === 0 ? (
          <TouchableOpacity
            onPress={useLoopFlow}
            accessibilityRole="button"
            accessibilityLabel={translate("Reset to the 4 sections")}
            style={[styles.standardButton, { borderColor: colors.accentPrimary }]}
          >
            <MaterialCommunityIcons name="auto-fix" size={14} color={colors.accentPrimary} />
            <Text style={[styles.standardText, { color: colors.accentPrimary }]}>
              {translate("Reset to the 4 sections")}
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {errors.steps ? (
        <Text style={[styles.error, { color: colors.danger }]}>{translate(errors.steps)}</Text>
      ) : null}

      {steps.map((step, index) => (
        <WizardPhaseCard
          key={step.step_key}
          colors={colors}
          step={step}
          index={index}
          total={steps.length}
          // Every block id in the LESSON, not just this step's. A new block
          // takes the first free `type-N`, and counting only the current step
          // lets step 5 mint an id step 2 already holds — which the seeder and
          // `verify_lessons.py` both reject, lesson-wide, at save or seed time.
          lessonBlockIds={steps.flatMap((s) =>
            (Array.isArray(s.blocks) ? s.blocks : []).map((b) => b.id)
          )}
          sectionOptions={SECTION_OPTIONS}
          concepts={concepts}
          boardTopicId={boardTopicId}
          catalog={catalog}
          expanded={expandedKey === step.step_key}
          onToggle={() =>
            setExpandedKey(expandedKey === step.step_key ? null : step.step_key)
          }
          onChange={(next) => onChange(steps.map((s, i) => (i === index ? next : s)))}
          onRemove={() => onChange(steps.filter((_, i) => i !== index))}
          onMove={(delta) => move(index, delta)}
          t={t}
        />
      ))}

      <TouchableOpacity
        onPress={() => addStep()}
        accessibilityRole="button"
        accessibilityLabel={translate("Add step")}
        style={[styles.addStep, { borderColor: colors.borderStrong }]}
      >
        <MaterialCommunityIcons name="plus" size={16} color={colors.accentPrimary} />
        <Text style={[styles.addStepText, { color: colors.accentPrimary }]}>
          {translate("Add step")}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  introRow: { flexDirection: "row", alignItems: "center", marginBottom: 12, gap: 10 },
  introTitle: { fontSize: 15, fontWeight: "800" },
  introSubtitle: { fontSize: 12, marginTop: 2 },
  standardButton: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 7,
    gap: 5,
  },
  standardText: { fontSize: 11, fontWeight: "700" },

  addStep: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 11,
    borderWidth: 1,
    borderStyle: "dashed",
    paddingVertical: 12,
    gap: 6,
  },
  addStepText: { fontSize: 13, fontWeight: "700" },
  error: { fontSize: 12, fontWeight: "600", marginBottom: 10 },
});

export default WizardStepPhases;
